import { Component, OnInit, AfterViewInit, forwardRef, ViewChild, EventEmitter, ElementRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseFormComponent } from 'src/app/widgets/global/BaseFormComponent';
import { FormGroup, FormControl, Validators, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { RegexValidator } from 'src/app/validators/regex-validator';
import { PhoneAreaCodeValidator } from 'src/app/validators/phone-area-code-validator';
import { PatternValidator } from '../../global/Validators';
import { LocationService } from 'src/app/services/location.service';
import { UrlValidator } from 'src/app/validators/url-validator';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { GoogleMapsModule } from '@angular/google-maps';
import { environment } from 'src/environments/environment';

interface GoogleMapsPlace {
  place_id?: string;
  geometry?: {
    location: {
      lat(): number;
      lng(): number;
    };
  } | null;
  formatted_address?: string;
  name?: string;
}

interface GoogleMapsAutocomplete {
  addListener(event: string, callback: () => void): void;
  getPlace(): GoogleMapsPlace;
}

declare const google: {
  maps: {
    places: {
      Autocomplete: new (input: HTMLInputElement, options: Record<string, unknown>) => GoogleMapsAutocomplete;
    };
  };
};

export enum Appearance {
  STANDARD = 'standard',
  FILL = 'fill',
  OUTLINE = 'outline',
  LEGACY = 'legacy',
}

@Component({
  selector: 'app-venue-form',
  templateUrl: './venue-form.component.html',
  styleUrls: ['./venue-form.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    GoogleMapsModule,
  ],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => VenueFormComponent),
    multi: true
  }]
})
export class VenueFormComponent extends BaseFormComponent implements OnInit, AfterViewInit {

  // selectedAddress = '';
  counties: unknown;
  locations: unknown;

  latitude = 53.349979;
  longitude = -6.259960;
  zoom = 11;

  private geoCoder: unknown;

  @ViewChild('search', {static:false})
  public searchElementRef: ElementRef;

  autoCompleteOptions: Record<string, unknown> = {};
  addressLabelText = 'Address';
  placeholderText = 'Please enter the address';
  requiredErrorText = 'The address is required';
  invalidErrorText = 'The address is not valid';
  appearance: string | Appearance = Appearance.OUTLINE;
  address: unknown;
  country = 'ie';
  placeIdOnly?: boolean;
  strictBounds?: boolean;
  types?: string[] = [];
  type = 'address';
  isValidPlace = false;
  public showVenueName = false;
  public mapsLoaded = false;

  private onNewPlaceResult = new EventEmitter<unknown>();

  ngOnInit() {
    const options: Record<string, unknown> = {
      componentRestrictions: {country: this.country},
      placeIdOnly: this.placeIdOnly,
      strictBounds: this.strictBounds,
      types: this.types,
      type: this.type
    };

    this.autoCompleteOptions = Object.assign(this.autoCompleteOptions, options);
  }

  ngAfterViewInit() {
    this.loadGoogleMapsScript().then(() => {
      this._ngZone.run(() => {
        this.mapsLoaded = true;
      });
      this.initGoogleMapsAutocomplete();
    });
  }

  private loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve) => {
      if (typeof google !== 'undefined' && google.maps) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }


  constructor(private regexValidator: RegexValidator, private locationService: LocationService, private phoneAreaCodeValidator: PhoneAreaCodeValidator, private urlValidator: UrlValidator, private _ngZone: NgZone) {
    super();

    this.loadData(this.locationService)

    this.form = new FormGroup({
      name: new FormControl('', [Validators.required, PatternValidator.noWhiteSpace()]),
      address: new FormControl('', Validators.compose([Validators.required, PatternValidator.noWhiteSpace()])),
      googleplaceid: new FormControl(''),
      latitude: new FormControl(''),
      longitude: new FormControl('')
    });

    this.onValueChanged(p => {
      this.setValues(p.name as string, p.address as string, p.googleplaceid as string, p.latitude as string, p.longitude as string);
    });


    this.form.valueChanges.subscribe(values => {
      this.value.name = values.name;
      this.value.address = values.address;
      this.value.googleplaceid = values.googleplaceid;
      this.value.latitude = values.latitude;
      this.value.longitude = values.longitude;
    });

  }


  async loadData(locationService: LocationService): Promise<void> {

    this.counties = await locationService.getCounties();

  }



  setValues(name: string, address: string, googleplaceid: string, latitude: string, longitude: string): void {
    this.form.get('name').setValue(name);
    this.form.get('address').setValue(address);
    this.form.get('googleplaceid').setValue(googleplaceid);
    this.form.get('latitude').setValue(latitude);
    this.form.get('longitude').setValue(longitude);
  }


  onLocationSelected(location: { latitude: number; longitude: number }) {
    this.latitude = location.latitude;
    this.longitude = location.longitude;
  }



  public initGoogleMapsAutocomplete() {
    if (typeof google === 'undefined' || !google.maps) {
      return;
    }
    const inputEl = this.searchElementRef.nativeElement as HTMLInputElement;
    const autocomplete = new google.maps.places.Autocomplete(inputEl, this.autoCompleteOptions);

    // When Enter is pressed, simulate Down Arrow first to select the first autocomplete suggestion
    inputEl.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        // If the pac-container is visible (suggestions showing), simulate ArrowDown to highlight first item
        const pacContainer = document.querySelector('.pac-container') as HTMLElement;
        if (pacContainer && pacContainer.offsetParent !== null) {
          const downArrow = new KeyboardEvent('keydown', {
            key: 'ArrowDown',
            code: 'ArrowDown',
            keyCode: 40,
            which: 40,
            bubbles: true,
            cancelable: true
          });
          inputEl.dispatchEvent(downArrow);
        }
      }
    });

    autocomplete.addListener('place_changed', () => {
      this._ngZone.run(() => {
        // get the place result
        const place = autocomplete.getPlace();

            if (!place.place_id || place.geometry === undefined || place.geometry === null) {
              this.resetAddress();
              return;
            }

            this.isValidPlace = true;
            const selectedAddress = place.formatted_address.startsWith(place.name) ? place.formatted_address : `${place.name + ', '}${place.formatted_address}`;
            this.form.get('name').setValue(place.name);
            this.showVenueName = true;
            this.form.get('address').setValue(selectedAddress);

            if(place.geometry && place.geometry.location){
              this.form.get('latitude').setValue(place.geometry.location.lat());
              this.form.get('longitude').setValue(place.geometry.location.lng());
            }

            if (place.place_id) {
              this.form.get('googleplaceid').setValue(place.place_id);
            }

            // console.log('onAutocompleteSelected -> ', place);
            this.onLocationSelected(
              {
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng()
              });
          });
        });
  }


  private resetAddress() {
    this.form.get('address').setValue(null);
    this.isValidPlace = false;
    this.form.get('address').updateValueAndValidity();
  }
}
